/* eslint-disable multiline-ternary */
import React, { useState, Fragment, useRef, useEffect } from 'react'

import { useSelector } from 'react-redux'

import { mainTableStyles } from '../../styles/classes/FieldFormClasses'
import {
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
  LinearProgress
} from '@mui/material'
import { SortMenu } from './SortMenu'
import ConfirmationModal from '../general/ConfirmationModal'
import { useTranslation } from 'react-i18next'
import { capitalize } from '../../utils/global'
import moment from 'moment'
import {
  defaultValues,
  statusOptions,
  userRolesStrings
} from '../../utils/fieldForms'

const columns = [
  { id: 'form_name', visible: true },
  { id: 'status', visible: true },
  { id: 'last_edit_date', visible: true },
  { id: 'edited_by', visible: true },
  { id: 'trade', visible: true },
  { id: 'service', visible: true },
  { id: 'type', visible: true },
  { id: 'actions', visible: true }
]

const MainTable = ({ items, loading, onAction, onScrollBottom }) => {
  const tableContainerRef = useRef(null)
  const userRole = useSelector(state => state.auth.userRole)
  const { classes } = mainTableStyles()
  const { t } = useTranslation()
  const noSort = ['form_name', 'actions']
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [rowForAction, setRowForAction] = useState(null)
  const [action, setAction] = useState(null)
  // modal
  const [mainActionLabel, setMainActionLabel] = useState('')
  const [secondaryActionLabel, setSecondaryActionLabel] = useState('')
  const [title, setTitle] = useState(null)
  const [message, setMessage] = useState(null)
  const [extraMessage, setExtraMessage] = useState(null)
  const scrollPosition = useRef(0)

  useEffect(() => {
    const tableContainer = tableContainerRef.current
    if (!tableContainer || !onScrollBottom) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          scrollPosition.current = tableContainerRef.current.scrollTop
          onScrollBottom()
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 1.0
      }
    )

    const sentinel = document.createElement('div')
    tableContainer.appendChild(sentinel)
    observer.observe(sentinel)

    return () => {
      observer.unobserve(sentinel)
      tableContainer.removeChild(sentinel)
    }
  }, [onScrollBottom])

  useEffect(() => {
    if (tableContainerRef.current) {
      setTimeout(() => {
        tableContainerRef.current.scrollTop = scrollPosition.current
      }, 50)
    }
  }, [items])

  const mainActionModal = () => {
    onAction(action, rowForAction)
    setShowModal(false)
    setRowForAction(null)
    setAction(null)
  }

  const secondaryActionModal = () => {
    setShowModal(false)
    setRowForAction(null)
    setAction(null)
  }

  // remove modal data setter
  const openRemoveModalHandler = row => {
    setTitle(t('field_forms.remove_modal.title'))
    setMessage(
      t('field_forms.remove_modal.message', { formName: row.form_name })
    )
    setExtraMessage(t('field_forms.remove_modal.extra_message'))
    setMainActionLabel(t('field_forms.continue'))
    setSecondaryActionLabel(t('field_forms.cancel'))
    setAction('remove')
    setRowForAction(row)
    setShowModal(true)
  }

  // clone modal data setter
  const openCloneModalHandler = row => {
    setTitle('Clone Field Form')
    setMessage(
      t('field_forms.clone_modal.message', { formName: row.form_name })
    )
    setExtraMessage(t('field_forms.clone_modal.extra_message'))
    setMainActionLabel(t('field_forms.continue'))
    setSecondaryActionLabel(t('field_forms.cancel'))
    setAction('clone')
    setRowForAction(row)
    setShowModal(true)
  }

  const createSortHandler = (order, field) => {
    setOrder(order)
    setOrderBy(field)
  }

  const descendingComparatorAlt = (a, b, orderBy, fieldInsideObject) => {
    const valA = a[orderBy][fieldInsideObject]
    const valB = b[orderBy][fieldInsideObject]

    if (valB == null && valA != null) return 1
    if (valA == null && valB != null) return -1
    if (valA == null && valB == null) return 0

    if (typeof valB === 'string' && typeof valA === 'string') {
      return valB.localeCompare(valA) // proper string comparison
    }

    if (valB < valA) return 1
    if (valB > valA) return -1
    return 0
  }

  const descendingComparator = (a, b, orderBy, mapFun = (txt) => txt) => {
    const valA = mapFun(a[orderBy])
    const valB = mapFun(b[orderBy])

    if (valB == null && valA != null) return 1
    if (valA == null && valB != null) return -1
    if (valA == null && valB == null) return 0

    if (typeof valB === 'string' && typeof valA === 'string') {
      return valB.localeCompare(valA) // descending order
    }

    if (valB < valA) return 1
    if (valB > valA) return -1
    return 0
  }

  const getComparator = (order, orderBy) => {
    if (orderBy === 'last_edit_date') {
      return order === 'desc'
        ? (a, b) => descendingComparatorAlt(a, b, 'last_modified', 'date')
        : (a, b) => -descendingComparatorAlt(a, b, 'last_modified', 'date')
    }
    if (orderBy === 'edited_by') {
      return order === 'desc'
        ? (a, b) => descendingComparatorAlt(a, b, 'last_modified', 'firstName')
        : (a, b) => -descendingComparatorAlt(a, b, 'last_modified', 'firstName')
    }
    if (orderBy === 'status') {
      const mapStatus = (status) => (status === 'active' ? 'published' : status)
      return order === 'desc'
        ? (a, b) => descendingComparator(a, b, 'status', mapStatus)
        : (a, b) => -descendingComparator(a, b, 'status', mapStatus)
    }
    if (orderBy === 'trade') {
      const mapTrade = (trade) => (trade === 'Default' ? 'All Trades' : trade)
      return order === 'desc'
        ? (a, b) => descendingComparator(a, b, 'trade', mapTrade)
        : (a, b) => -descendingComparator(a, b, 'trade', mapTrade)
    }
    if (orderBy === 'service') {
      const mapService = (service) => (service === 'Default' ? 'All Services' : service)
      return order === 'desc'
        ? (a, b) => descendingComparator(a, b, 'service', mapService)
        : (a, b) => -descendingComparator(a, b, 'service', mapService)
    }
    if (orderBy === 'type') {
      const mapType = (type) => (type === 'Default' ? 'All Types' : type)
      return order === 'desc'
        ? (a, b) => descendingComparator(a, b, 'work_type', mapType)
        : (a, b) => -descendingComparator(a, b, 'work_type', mapType)
    }
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  // Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
  // stableSort() brings sort stability to non-modern browsers (notably IE11). If you
  // only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
  // with exampleArray.slice().sort(exampleComparator)
  const stableSort = (array, comparator) => {
    if (!array) return []
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) {
        return order
      }
      return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
  }

  const progressRow = (columns, i) => (
    <TableRow
      key={`progress-${i}`}
      id={`progress-${i}`}
      onClick={() => null}
      tabIndex={-1}
      className={classes.tableRowSkeleton}
    >
      {columns?.map((column, index) => {
        if (!column.visible) {
          return null
        }
        return (
          <TableCell
            key={column.id + '-' + i + '-' + index}
            align="left"
            className={
              column.id === 'form_name'
                ? classes.formCellSkeleton
                : classes.tableCellSkeleton
            }
          >
            <LinearProgress
              className={classes.linearProgressStyle}
              classes={{
                colorPrimary: classes.colorPrimary,
                barColorPrimary: classes.barColorPrimary,
                root: classes.dimensions
              }}
            />
          </TableCell>
        )
      })}
    </TableRow>
  )

  const progressComponent = columns => {
    const auxArray = []
    for (let i = 1; i <= 4; i++) {
      auxArray.push(progressRow(columns, i))
    }
    return <TableBody>{auxArray.map((obj, ind) => obj)}</TableBody>
  }

  // Table Head
  const tableHead = () => {
    return (
      <TableHead>
        <TableRow>
          {columns.map(column => {
            if (
              column.id === 'actions' &&
              userRole?.name === userRolesStrings.VIEW_ONLY
            ) {
              return null
            }
            let cellClasses = classes.tableCell
            if (column.id === 'form_name') cellClasses = classes.tableCellName
            if (column.id === 'actions') cellClasses = classes.tableCellActions
            return (
              <TableCell
                key={column.id}
                className={cellClasses}
                sortDirection={orderBy === column.id ? order : false}
              >
                <Box
                  className={classes.tableSortLabel}
                  active={false}
                  hideSortIcon
                >
                  {t('field_forms.column_names.' + column.id)}
                  {!noSort.includes(column.id) && (
                    <SortMenu
                      id={column.id}
                      onSelectSort={createSortHandler}
                      order={order}
                      orderBy={orderBy}
                    />
                  )}
                </Box>
              </TableCell>
            )
          })}
        </TableRow>
      </TableHead>
    )
  }

  // Table Body
  const tableBody = () => {
    return (
      <TableBody key="wo-table-body">
        {stableSort(items, getComparator(order, orderBy)).map(row => {
          // format
          let tradeName = row.trade
          let serviceName = row.service
          let workTypeName = row.work_type

          // do we need translations for this? yes add translations
          if (tradeName?.toLowerCase() === defaultValues.TRADE) {
            tradeName = t('field_forms.all_trades')
          }

          if (serviceName?.toLowerCase() === defaultValues.SERVICE) {
            serviceName = t('field_forms.all_services')
          }

          if (workTypeName?.toLowerCase() === defaultValues.WORK_TYPE) {
            workTypeName = t('field_forms.all_types')
          }
          const status =
            row.status === statusOptions.PUBLISHED
              ? 'Published'
              : capitalize(row?.status)
          let canDelete = true
          if (
            row.status === statusOptions.PUBLISHED
          ) {
            canDelete = false
          }

          const lastEditDate = moment
            .unix(row.last_modified.date)
            .format('MM/DD/Y HH:mmA')
          const editedBy = `${
            capitalize(row?.last_modified?.firstName) ?? ''
          } ${capitalize(row?.last_modified?.lastName ?? '')}`

          return (
            <TableRow key={row.id}>
              <TableCell className={classes.tableCellName}>
                <Typography
                  className={`${classes.formTitle} ${classes.truncateTexOneLine}`}
                >
                  {row.form_name}
                </Typography>
                <Typography
                  className={`${classes.formDescription} ${classes.truncateText}`}
                >
                  {row.form_description}
                </Typography>
              </TableCell>

              <TableCell className={classes.tableCell}>
                <Typography className={classes.tableText}>{status}</Typography>
              </TableCell>

              <TableCell className={classes.tableCell}>
                <Typography className={classes.tableText}>
                  {lastEditDate}
                </Typography>
              </TableCell>

              <TableCell className={classes.tableCell}>
                <Typography className={classes.tableText}>
                  {editedBy}
                </Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography className={classes.tableText}>
                  {tradeName}
                </Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography className={classes.tableText}>
                  {serviceName}
                </Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography className={classes.tableText}>
                  {workTypeName}
                </Typography>
              </TableCell>
              {userRole?.name === userRolesStrings.VIEW_ONLY ? null : (
                <TableCell className={classes.tableCellActions}>
                  <Box sx={{ display: 'flex' }}>
                    <Chip
                      name="edit"
                      size="small"
                      className={classes.chip}
                      label={t('field_forms.edit')}
                      variant="outlined"
                      onClick={() => {
                        onAction('edit', row)
                      }}
                    />
                    <Chip
                      size="small"
                      className={classes.chip}
                      label={t('field_forms.clone')}
                      variant="outlined"
                      onClick={() => {
                        openCloneModalHandler(row)
                      }}
                    />
                    {canDelete && (
                      <Chip
                        size="small"
                        className={classes.chip}
                        label={t('field_forms.delete')}
                        variant="outlined"
                        onClick={() => {
                          openRemoveModalHandler(row)
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
              )}
            </TableRow>
          )
        })}
      </TableBody>
    )
  }

  const emptyQueryMessage = () => {
    return (
      <TableBody key="empty-body">
        <TableRow>
          <TableCell className={classes.emptyMessageCell} colSpan={8}>
            <Box className={classes.emptyQueryBody}>
              {t('field_forms.empty_query_message')}
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
    )
  }

  return (
    <Fragment>
      <TableContainer
        className={classes.tableContainer}
        ref={tableContainerRef}
      >
        <Table stickyHeader aria-label="sticky table">
          {!loading && tableHead()}
          {loading && progressComponent(columns)}
          {!loading && items?.length !== 0 && tableBody()}
          {!loading && items?.length === 0 && emptyQueryMessage()}
        </Table>
        {items?.length !== 0 && <div className={classes.separator} />}
      </TableContainer>
      {/* Modal */}
      <ConfirmationModal
        title={title}
        message={message}
        extraMessage={extraMessage}
        open={showModal}
        onMainAction={mainActionModal}
        onSecondaryAction={secondaryActionModal}
        mainActionLabel={mainActionLabel}
        secondaryActionLabel={secondaryActionLabel}
      />
    </Fragment>
  )
}

export default MainTable
